import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { usePermissionGuardRedirect } from "./usePermissionGuard";
import {
    AddProgram,
    GetAssignedProgrammeAndShortCourseList,
    GetDropdownForProgrammeAndShortCourse,
    UpdateProgramme,
    getActiveVersionIds,
    getAssignedDropdownForPmc,
    GetListProgrammeAndShortCourse,
} from "../services/ProgramService";
import { mapBuildingsToApi, mapBuildingsToSelect } from "../utils/CampusBuildingsUtil";
import {
    useAssignPageList,
    useCampusBuildingsList,
    useNetsuiteProductDropdown,
    usePageFiltersForPmc,
    useProgrammeSummary,
    useSchoolList,
} from "../query/hooks/QueryHooksProgramDetail";
import { invalidateVersionListQueries, usePmcVersionListForFilters } from "../query/hooks/QueryHooksLinkedProgrammes";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

/**
 * Drives the PMC Programme detail/edit form: local field state, formik
 * config, dropdown/summary data fetching, articulation/assigned lists,
 * and unsaved-changes tracking.
 * @param {object} options Hook options.
 * @param {object} options.detail The programme detail record being edited.
 * @param {Function} options.setRefresh Triggers a re-fetch of the programme detail.
 * @param {Function} options.setQAview Toggles the QA/versioned view.
 */
function useProgramDetail({ detail, setRefresh, setQAview }) {
    const history = useHistory();
    const { id, type } = useParams();
    const [disabled, setDisabled] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [programmeAdded, setProgrammeAdded] = useState(false);
    const [editorResetKey, setEditorResetKey] = useState(0);

    const [subjectContent, setSubjectContent] = useState("");
    const [admissionReq, setAdmissionReq] = useState("");
    const [accreditation, setAccreditation] = useState();
    const [articulationOption, setArticulationOption] = useState("");

    const [studyKit, setStudyKit] = useState("");
    const [careerOpportunity, setCareerOpportunity] = useState("");
    const [hardwareReq, setHardwareReq] = useState("");
    const [duration, setDuration] = useState("");
    const [regulatoryInfo, setRegulatoryInfo] = useState("");

    const [richContentTouched, setRichContentTouched] = useState({});
    const markRichContentTouched = (field) => {
        setRichContentTouched((current) =>
            current[field] ? current : { ...current, [field]: true }
        );
    };
    const resetRichContentTouched = () => setRichContentTouched({});
    const [impNotes, setImpNotes] = useState("");
    const [editorToShow, setEditorToShow] = useState(false);

    const [activeVersionList, setActiveVersionList] = useState([]);

    const [allpageListData, setAllPageListData] = useState([]);
    const [pageStatus, setPageStatus] = useState({ arr: [], checkObj: {} });
    const [pageBrand, setPageBrand] = useState({ arr: [], checkObj: {} });
    const [loading, setLoading] = useState(false);
    const [assignPageTableState, setAssignPageTableState] = useState({
        page: 1,
        perPage: 10,
        sortKey: "PageTitle",
        sortOrder: "asc",
    });
    const [assignPageSearch, setAssignPageSearch] = useState("");

    const [articulationOptionList, setArticulationOptionList] = useState([]);
    const [articulationOptionLoading, setArticulationOptionLoading] = useState(false);
    const [articulationOptionSearch, setArticulationOptionSearch] = useState("");
    const [articulationOptionTotalRow, setArticulationOptionTotalRow] = useState("");
    const [articulationOptionTableState, setArticulationOptionTableState] = useState({
        page: 1,
        perPage: 10,
        sortKey: "name",
        sortOrder: "asc",
    });
    const [artStatus, setArtStatus] = useState({ arr: [], checkObj: {} });
    const [artProgType, setArtProgType] = useState({ arr: [], checkObj: {} });
    const [articulationOptionFilterData, setArticulationOptionFilterData] = useState({ 
        status: [], 
        programmeType: [], 
        type: [] 
    });
    const [artType, setArtType] = useState({ arr: [], checkObj: {} });

    const [savedAssignedArticulationOptionList, setSavedAssignedArticulationOptionList] = useState([]);
    const assignedDataList = useMemo(
        () => savedAssignedArticulationOptionList?.map(({ id, type, pmc_type }) => ({
            id,
            pmc_type: type || pmc_type,
        })),
        [savedAssignedArticulationOptionList]
    );
    const [assignedArticulationOptionList, setAssignedArticulationOptionList] = useState([]);
    const [assignedArticulationSearch, setAssignedArticulationSearch] = useState("");
    const [assignedStatus, setAssignedStatus] = useState({ arr: [], checkObj: {} });
    const [assignedProgType, setAssignedProgType] = useState({ arr: [], checkObj: {} });
    const [assignedType, setAssignedType] = useState({ arr: [], checkObj: {} });
    const [assignedArticulationOptionFilterData, setAssignedArticulationOptionFilterData] = useState({ 
        AssignStatus: [], 
        AssignProgrammeType: [], 
        AssignType: [] 
    });
    const [updateData, setUpdateData] = useState(false);

    const dropDownValues = useSelector((state) => state.registerDropdownValues);

    const { data: versionList, isLoading: versionListQueryLoading } = usePmcVersionListForFilters(id);
    // No programme id yet (e.g. creating a new PMC): nothing to load, so don't
    // report loading - a disabled query's isLoading otherwise stays true forever.
    const versionListLoading = Boolean(id) && versionListQueryLoading;

    const { data: schoolList } = useSchoolList();
    const { data: netsuiteProdList } = useNetsuiteProductDropdown();
    const { data: PageFilterData } = usePageFiltersForPmc();
    const { data: campusBuildingsList } = useCampusBuildingsList();
    const { data: summaryTotals, isLoading: summaryTotalsLoading } = useProgrammeSummary(id);

    // activeRef is a supersession guard, set to false by the calling effect's
    // cleanup so a resolved-but-superseded call skips its setState.
    const fetchActiveVersion = async (id, activeRef) => {
        try {
            const res = await getActiveVersionIds(id);
            if (activeRef.current) {
                setActiveVersionList(res?.data?.getVersionDropdown);
            }
        } catch (error) {
            console.log("useProgramDetail.js->fetchActiveVersion->Error", error);
        }
    };

    const assignPageRequestData = useMemo(() => ({
        page: assignPageTableState.page,
        perPage: assignPageTableState.perPage,
        search: assignPageSearch,
        key: assignPageTableState.sortKey,
        sort: assignPageTableState.sortOrder,
        exportStatus: false,
        viaBrand: pageBrand.arr,
        viaStatus: pageStatus.arr,
    }), [assignPageTableState, assignPageSearch, pageBrand, pageStatus]);

    const {
        data: { data: assignPageList, total: assignPageTotalRows },
        isLoading: assignPageLoading,
    } = useAssignPageList(assignPageRequestData);

    useEffect(() => {
        if (detail) {
            setSubjectContent(detail.Description);
            setAdmissionReq(detail.admission_req);
            setAccreditation(detail.accreditation);
            setArticulationOption(detail.articultion_option);
            setCareerOpportunity(detail.career_opportunities);
            setStudyKit(detail.study_kit);
            setHardwareReq(detail.hardware_requirement);
            setDuration(detail.duration_dec);
            setRegulatoryInfo(detail.regulatory_inform);
            setImpNotes(detail.important_notes);
            setEditorToShow(true);
        }
    }, [detail]);

    usePermissionGuardRedirect(['pmcpedit']);

    useEffect(() => {
        let active = true;

        const fetchAssignedArticulationList = async () => {
            setLoading(true);
            try {
                const payload = {
                    id: id,
                    exportStatus: "true",
                    type_assigned: 0,
                };
                const res = await GetAssignedProgrammeAndShortCourseList(payload);
                if (!active) {
                    return;
                }
                if (res.status === 200) {
                    const respData = res.data.data;
                    setAssignedArticulationOptionList(respData.resData);
                    setSavedAssignedArticulationOptionList(respData.resData);
                }
            } catch (err) {
                if (active) {
                    console.log("useProgramDetail.js->fetchAssignedArticulationList->Error", err);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        if (type === "open" || type === "duplicate") {
            fetchAssignedArticulationList();
        }

        return () => {
            active = false;
        };
    }, [id, updateData, type]);

    useEffect(() => {
        let active = true;

        const fetchArticulationOptionList = async () => {
            setArticulationOptionLoading(true);
            try {
                const payload = {
                    page: articulationOptionTableState.page,
                    limit: articulationOptionTableState.perPage,
                    key: articulationOptionTableState.sortKey,
                    sort: articulationOptionTableState.sortOrder,
                    exportStatus: false,
                    search: articulationOptionSearch,
                    viaStatus: artStatus.arr,
                    viaType: artType.arr,
                    viaProgrammeType: artProgType.arr,
                    id: id,
                };
                const res = await GetListProgrammeAndShortCourse(payload);
                if (!active) {
                    return;
                }
                if (res.status === 200) {
                    const respData = res.data.data;
                    setArticulationOptionList(respData.resData);
                    setArticulationOptionTotalRow(respData.pagination.total);
                }
            } catch (err) {
                if (active) {
                    console.log("useProgramDetail.js->fetchArticulationOptionList->Error", err);
                }
            } finally {
                if (active) {
                    setArticulationOptionLoading(false);
                }
            }
        };

        fetchArticulationOptionList();

        return () => {
            active = false;
        };
    }, [
        articulationOptionTableState,
        artType,
        artProgType,
        artStatus,
        articulationOptionSearch,
        id,
    ]);

    useEffect(() => {
        let active = true;

        const fetchArticulationOptionDropdown = async () => {
            try {
                const res = await GetDropdownForProgrammeAndShortCourse();
                if (!active) {
                    return;
                }
                if (res.status === 200) {
                    const respData = res.data;
                    setArticulationOptionFilterData({
                        ...res.data,
                        status: respData.status,
                        programmeType: respData.programmeType,
                        type: respData.type,
                    });
                }
            } catch (err) {
                if (active) {
                    console.log("useProgramDetail.js->fetchArticulationOptionDropdown->Error", err);
                }
            }
        };

        const fetchAssignedArticulationDropdown = async () => {
            try {
                const res = await getAssignedDropdownForPmc({
                    id: id,
                    type_assigned: 0,
                });
                if (!active) {
                    return;
                }
                if (res.status === 200) {
                    const respData = res.data;
                    setAssignedArticulationOptionFilterData({
                        ...res.data,
                        AssignStatus: respData.status,
                        AssignProgrammeType: respData.programmeType,
                        AssignType: respData.type,
                    });
                }
            } catch (err) {
                if (active) {
                    console.log("useProgramDetail.js->fetchAssignedArticulationDropdown->Error", err);
                }
            }
        };

        fetchArticulationOptionDropdown();

        if (type === "open" || type === "duplicate") {
            fetchAssignedArticulationDropdown();
        }

        return () => {
            active = false;
        };
    }, [updateData, id, type]);

    const formik = useFormik({
        enableReinitialize: type === "open" || type === "duplicate" ? true : false,
        initialValues: {
            duplicateId: "",
            name: detail?.name ? detail?.name : "",
            saqa_id: detail?.saqa_id ? detail.saqa_id : "",
            SAQA_Name: detail?.SAQA_Name ? detail.SAQA_Name : "",
            School: detail?.School ? detail.School : "",
            qualification_type: detail?.qualification_type ? detail?.qualification_type : "",
            Code: detail?.Code ? detail.Code : "",
            SAQA_Link: detail?.SAQA_Link ? detail.SAQA_Link : "",
            NQF_Level: detail?.NQF_Level ? detail.NQF_Level : "",
            min_admission_level: detail?.min_admission_level ? detail.min_admission_level : "",
            Delivery_Methods: detail?.getLearningMethods
                ? detail?.getLearningMethods?.map((el) => el?.learning_method)
                : [],
            campusBuildings: detail?.campusBuildings ? mapBuildingsToSelect(detail.campusBuildings) : [],
            brandID: detail?.getBrandID ? detail.getBrandID?.map((el) => el?.brand_id) : [],
            FTMax: detail?.FTMax ? detail.FTMax : 0,
            FTMin: detail?.FTMin ? detail.FTMin : 0,
            PTMax: detail?.PTMax ? detail.PTMax : 0,
            PTMin: detail?.PTMin ? detail.PTMin : 0,
            Credits: detail?.Credits ? detail.Credits : "",
            Duration: detail?.Duration ? detail.Duration : "",
            Term_Period: detail?.Term_Period ? detail.Term_Period : "",
            version_id: detail?.version_id ? detail.version_id : "",
            netsuite_product: detail?.netsuite_product ? detail.netsuite_product : "",
            netsuite_department_id: detail?.netsuite_department_id ? detail.netsuite_department_id : "",
            status: detail?.status ? detail.status : "",
            popupID: detail?.popupID ? detail.popupID : "",
            auto_generate_fectsheet: Number(detail?.auto_generate_fectsheet) === 1,
            override_default_tc: Number(detail?.override_default_tc) === 1,
            EnableStudypathLink: Number(detail?.EnableStudypathLink) === 1,
            EnableStudypathLinkReReg: Number(detail?.EnableStudypathLinkReReg) === 1,
            articulationMandatory: Number(detail?.articulationMandatory) === 1,
            terms_conditions_page: detail?.terms_conditions_page ? detail?.terms_conditions_page : "",
            terms_conditions_page_type: detail?.terms_conditions_page_type ? detail?.terms_conditions_page_type : "",
            Description: "",
            admission_req: "",
            accreditation: "",
            articultion_option: "",
            articulation_assign: assignedDataList,
            career_opportunities: "",
            study_kit: "",
            reRegFrequency: detail?.reRegFrequency ? detail.reRegFrequency : 0,
            hardware_requirement: "",
            core_spec: detail?.core_spec ? detail?.core_spec : 0,
            med_spec: detail?.med_spec ? detail?.med_spec : 0,
            high_spec: detail?.high_spec ? detail?.high_spec : 0,
            full_time_hybrid_fee: detail?.full_time_hybrid_fee ? detail?.full_time_hybrid_fee : "",
            full_time_online_fee: detail?.full_time_online_fee ? detail?.full_time_online_fee : "",
            part_time_online_fee: detail?.part_time_online_fee ? detail?.part_time_online_fee : "",
            registration_fee: detail?.registration_fee ? detail?.registration_fee : "",
            cost_terms: detail?.cost_terms ? detail?.cost_terms : "",
            installments: detail?.installments ? detail?.installments : "",
            earlyBirds: (type === "open" || type === "duplicate") && detail?.earlyBirds !== "" ? detail?.earlyBirds : 2,
            duration_dec: "",
            regulatory_inform: "",
            important_notes: "",
            conversion_code: detail?.conversion_code ? detail?.conversion_code : "",
            meta_title: detail?.meta_title ? detail?.meta_title : "",
            meta_description: detail?.meta_description ? detail?.meta_description : "",
            meta_keywords: detail?.meta_keywords ? detail?.meta_keywords : "",
        },
        validationSchema: Yup.object({
            PTMax: Yup.number().when("EnableStudypathLinkReReg", {
                is: true,
                then: Yup.number().when("PTMin", (PTMin, schema) =>
                    schema.test(
                        "pt-max-check",
                        "Maximum PT must be more than Minimum PT",
                        function (value) {
                            if (value === 0) {
                                // 0 = no maximum set, skip the min/max comparison
                                return true;
                            }
                            return value >= PTMin;
                        }
                    )
                ),
                otherwise: Yup.number().notRequired(),
            }),
            FTMax: Yup.number().when("EnableStudypathLinkReReg", {
                is: true,
                then: Yup.number().when("FTMin", (FTMin, schema) =>
                    schema.test(
                        "ft-max-check",
                        "Maximum FT must be more than Minimum FT",
                        function (value) {
                            if (value === 0) {
                                // 0 = no maximum set, skip the min/max comparison
                                return true;
                            }
                            return value >= FTMin;
                        }
                    )
                ),
                otherwise: Yup.number().notRequired(),
            }),
            name: Yup.string()
                .required("Programme Name is required")
                .max(255, "Programme Name should not be greater than 255 Character")
                .trim(),
            SAQA_Name: Yup.string()
                .required("SAQA Name is required")
                .max(255, "SAQA Name should not be greater than 255 Character")
                .trim(),
            School: Yup.string().required("School is required").trim(),
            qualification_type: Yup.string().required("Qualification Type is required"),
            NQF_Level: Yup.number()
                .typeError("NQF Level must be number")
                .min(1, "NQF can be 1 minimum")
                .max(10, "NQF can be 10 maximum")
                .required("NQF_Level is required"),
            Delivery_Methods: Yup.array().min(1, "Learning Methods is required"),
            status: Yup.string().required("Review Status is required"),
            version_id: Yup.number().when("EnableStudypathLinkReReg", {
                is: (val) =>
                    (type === "open" || type === "duplicate") && val === true && activeVersionList?.length > 0,
                then: (schema) => schema.required("Active Version is required"),
                otherwise: (schema) => schema.notRequired().nullable(),
            })
        }),
        onSubmit: async (values) => {
            setDisabled(true);
            const formData = new FormData();

            formData.append("name", values.name);
            formData.append("SAQA_Name", values.SAQA_Name);
            formData.append("saqa_id", values.saqa_id);
            formData.append("School", values.School);
            formData.append("qualification_type", values.qualification_type);
            formData.append("Code", values.Code);
            formData.append("SAQA_Link", values.SAQA_Link);
            formData.append("NQF_Level", values.NQF_Level);
            formData.append("min_admission_level", values.min_admission_level);
            values.Delivery_Methods.forEach((val, idx) => {
                formData.append(`${"Delivery_Methods"}[${idx}]`, val);
            });
            formData.append('reRegFrequency', values.reRegFrequency || 0);
            formData.append("Credits", values.Credits);
            formData.append("Duration", values.Duration);
            formData.append("FTMax", Number(values.FTMax || 0));
            formData.append("FTMin", Number(values.FTMin || 0));
            formData.append("PTMax", Number(values.PTMax || 0));
            formData.append("PTMin", Number(values.PTMin || 0));
            formData.append("Term_Period", values.Term_Period);
            formData.append("version_id", values.version_id);
            formData.append("campusBuildings", JSON.stringify(mapBuildingsToApi(values.campusBuildings)));
            formData.append("netsuite_product", values.netsuite_product);
            values.brandID.forEach((val, idx) => {
                formData.append(`${"brandID"}[${idx}]`, val);
            });
            formData.append("status", values.status);
            formData.append("publish_status", values.publish_status);
            formData.append("popupID", values.popupID);
            formData.append("override_default_tc", values.override_default_tc === true ? 1 : 0);
            formData.append("EnableStudypathLink", values.EnableStudypathLink === true ? 1 : 0);
            formData.append("EnableStudypathLinkReReg", values.EnableStudypathLinkReReg === true ? 1 : 0);
            formData.append("articulationMandatory", values.articulationMandatory === true ? 1 : 0);
            formData.append("terms_conditions_page", values.terms_conditions_page);

            formData.append("Description", subjectContent);
            formData.append("admission_req", admissionReq);
            formData.append("accreditation", accreditation);
            formData.append("articultion_option", articulationOption);
            formData.append("articulation_assign", JSON.stringify(values.articulation_assign));
            formData.append("career_opportunities", careerOpportunity);
            formData.append("study_kit", studyKit);
            formData.append("hardware_requirement", hardwareReq);
            formData.append("core_spec", values.core_spec);
            formData.append("med_spec", values.med_spec);
            formData.append("high_spec", values.high_spec);
            formData.append("full_time_hybrid_fee", values.full_time_hybrid_fee);
            formData.append("full_time_online_fee", values.full_time_online_fee);
            formData.append("part_time_online_fee", values.part_time_online_fee);
            formData.append("registration_fee", values.registration_fee);
            formData.append("cost_terms", values.cost_terms);
            formData.append("installments", values.installments);
            formData.append("earlyBirds", values.earlyBirds === "" ? 2 : values.earlyBirds);
            formData.append("duration_dec", duration);
            formData.append("regulatory_inform", regulatoryInfo);
            formData.append("important_notes", impNotes);
            formData.append("conversion_code", values.conversion_code);
            formData.append("meta_title", values.meta_title);
            formData.append("meta_description", values.meta_description);
            formData.append("meta_keywords", values.meta_keywords);
            formData.append("terms_conditions_page_type", values.terms_conditions_page_type);
            /*
             * Derived from the loaded Netsuite Product list rather than kept in sync via a
             * separate formik field/effect - the department id is only ever needed here, at
             * submit time, so there's nothing to gain from storing it as form state.
             */
            const netsuiteDepartmentId = values.netsuite_product
                ? netsuiteProdList.find((item) => item.value === values.netsuite_product)?.departmentId
                    ?? values?.netsuite_department_id
                : "";
            formData.append("netsuite_department_id", netsuiteDepartmentId ?? "");

            if (id && type !== "duplicate") {
                formData.append("programId", id);
                try {
                    await UpdateProgramme(formData);
                    setDisabled(false);
                    resetRichContentTouched();
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: "Updated Successfully",
                    });
                    setRefresh(Date.now());
                    /*
                     * `versionList`/`summaryTotals` are React-Query-cached independently of
                     * `refresh` (DEFAULT profile, 1-minute staleTime, refetchOnMount: false) -
                     * without this, a saved Active Version change (or any other version-count-
                     * affecting edit) would keep showing the previous version's badges/totals
                     * until that cache naturally expired. Same helper Version Details' own save
                     * already uses for the same reason.
                     */
                    try {
                        await invalidateVersionListQueries();
                    } catch (error) {
                        console.log("useProgramDetail.js->invalidateVersionListQueries->Error", error);
                    }
                    if (!values.EnableStudypathLinkReReg) {
                        setQAview(false);
                    }
                    setUpdateData((current) => !current);
                } catch (err) {
                    console.log("useProgramDetail.js->UpdateProgramme->Error", err);
                    if (err.response?.data?.id) {
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            html: `
                    PMC Programme already exists with specified Netsuite ID<br/>
                    <a
                        href="/program-management-center/programmes/programme/open/${err.response?.data?.id}"
                        className="customClass"
                        title="${err.response?.data?.name ? err.response.data.name.trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : ""}"
                        autofocus
                    >
                        ${err.response?.data?.name ? err.response.data.name.trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : "Unnamed"}
                    </a>
                `,
                        });
                        setDisabled(false);
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err?.response?.data?.message || "Something went wrong",
                        });
                        setDisabled(false);
                    }
                }
            } else {
                if (type === "duplicate") {
                    formData.append("duplicateId", id);
                }
                try {
                    await AddProgram(formData);
                    Swal.fire({
                        icon: "success",
                        title: "Success",
                        text: "Added Successfully",
                    });
                    setProgrammeAdded(true);
                } catch (err) {
                    console.log("useProgramDetail.js->AddProgram->Error", err);
                    if (err.response?.data?.id) {
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            html: `
                    PMC Programme already exists with specified Netsuite ID<br>
                    <a
                        href="/program-management-center/programmes/programme/open/${err.response?.data?.id}"
                        className="customClass"
                        title="${err.response?.data?.name ? err.response.data.name.trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : ""}"
                        autofocus
                    >
                        ${err.response?.data?.name ? err.response.data.name.trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : "Unnamed"}
                    </a>
                `,
                        });
                        setDisabled(false);
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err?.response?.data?.message || "Something went wrong",
                        });
                        setDisabled(false);
                    }
                }
            }
        },
    });

    useEffect(() => {
        const activeRef = { current: true };
        if (id && formik.values?.EnableStudypathLinkReReg) {
            fetchActiveVersion(id, activeRef);
        }
        return () => {
            activeRef.current = false;
        };
    }, [id, formik.values.EnableStudypathLinkReReg]);

    const { setFieldValue: setFormikFieldValue } = formik;

    useEffect(() => {
        const selectedBuildings = formik.values.campusBuildings;
        if (!selectedBuildings?.length || !campusBuildingsList?.length) {
            return;
        }

        const existingBuildingIds = new Set(campusBuildingsList.map((building) => Number(building.id)));
        const existingSelectedBuildings = selectedBuildings.filter((building) =>
            existingBuildingIds.has(building?.value)
        );
        if (existingSelectedBuildings.length !== selectedBuildings.length) {
            setFormikFieldValue("campusBuildings", existingSelectedBuildings);
        }
    }, [formik.values.campusBuildings, campusBuildingsList, setFormikFieldValue]);

    const handleChangeInstallments = (value) => {
        const prevValue = formik.values.installments.split(",").filter((item) => item !== "");
        if (prevValue.includes(value)) {
            const ind = prevValue.findIndex((item) => item === value);
            prevValue.splice(ind, 1);
        } else {
            prevValue.push(value);
        }
        formik.setFieldValue("installments", prevValue.join(","));
    };

    const richContentDirty = useMemo(() => {
        if (!detail) {
            return false;
        }

        return Object.values(richContentTouched).some(Boolean);
    }, [detail, richContentTouched]);

    const hasUnsavedChanges = Boolean(formik.dirty || richContentDirty);

    useUnsavedChangesGuard(hasUnsavedChanges, !programmeAdded, history);

    useEffect(() => {
        if (programmeAdded) {
            history.push(`/program-management-center/programmes/programme/table`);
        }
    }, [programmeAdded, history]);

    const restoreSavedValues = () => {
        formik.resetForm();
        setAssignedArticulationOptionList(savedAssignedArticulationOptionList);
        setSubjectContent(detail?.Description || "");
        setAdmissionReq(detail?.admission_req || "");
        setAccreditation(detail?.accreditation || "");
        setArticulationOption(detail?.articultion_option || "");
        setCareerOpportunity(detail?.career_opportunities || "");
        setStudyKit(detail?.study_kit || "");
        setHardwareReq(detail?.hardware_requirement || "");
        setDuration(detail?.duration_dec || "");
        setRegulatoryInfo(detail?.regulatory_inform || "");
        setImpNotes(detail?.important_notes || "");
        resetRichContentTouched();
        // CKEditor only reads its content on mount, so the editors are remounted to show the restored values.
        setEditorResetKey((current) => current + 1);
        setShowCancelConfirmation(false);
    };

    const handlereset = () => {
        if (type === "open") {
            if (hasUnsavedChanges) {
                setShowCancelConfirmation(true);
                return;
            }
        }
        history.push(`/program-management-center/programmes/programme/table`);
    };

    return {
        history,
        id,
        type,
        disabled,
        showCancelConfirmation,
        setShowCancelConfirmation,
        editorResetKey,
        markRichContentTouched,
        subjectContent,
        setSubjectContent,
        admissionReq,
        setAdmissionReq,
        accreditation,
        setAccreditation,
        articulationOption,
        setArticulationOption,
        studyKit,
        setStudyKit,
        careerOpportunity,
        setCareerOpportunity,
        hardwareReq,
        setHardwareReq,
        duration,
        setDuration,
        regulatoryInfo,
        setRegulatoryInfo,
        impNotes,
        setImpNotes,
        editorToShow,
        schoolList,
        netsuiteProdList,
        activeVersionList,
        versionList,
        versionListLoading,
        summaryTotals,
        summaryTotalsLoading,
        allpageListData,
        setAllPageListData,
        assignPageList,
        assignPageLoading,
        pageStatus,
        setPageStatus,
        pageBrand,
        setPageBrand,
        loading,
        PageFilterData,
        assignPageTableState,
        setAssignPageTableState,
        assignPageSearch,
        setAssignPageSearch,
        assignPageTotalRows,
        articulationOptionList,
        articulationOptionLoading,
        articulationOptionSearch,
        setArticulationOptionSearch,
        articulationOptionTotalRow,
        articulationOptionTableState,
        setArticulationOptionTableState,
        artStatus,
        setArtStatus,
        artProgType,
        setArtProgType,
        articulationOptionFilterData,
        artType,
        setArtType,
        assignedArticulationOptionList,
        setAssignedArticulationOptionList,
        assignedArticulationSearch,
        setAssignedArticulationSearch,
        assignedStatus,
        setAssignedStatus,
        assignedProgType,
        setAssignedProgType,
        assignedType,
        setAssignedType,
        assignedArticulationOptionFilterData,
        updateData,
        setUpdateData,
        dropDownValues,
        campusBuildingsList,
        formik,
        handleChangeInstallments,
        hasUnsavedChanges,
        restoreSavedValues,
        handlereset,
    };
};

export default useProgramDetail;